import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Id } from '../../../convex/_generated/dataModel'
import { Button } from '../common/Button'
import { useToast } from '../common/Toast'
import { formatScheduleText } from '../../utils/schedule-helpers'

interface MedicationData {
  _id: Id<'medications'>
  name: string
  timeSlot: string
  specificTime: string
  notes?: string
  frequency?: string
  frequencyDayOfWeek?: number
  frequencyOrdinal?: string
  frequencyDayOfMonth?: number
  oneTimeDate?: string
  remindDayBefore?: boolean
}

interface MedicationCardProps {
  medication: MedicationData
  onEdit: (medication: MedicationData) => void
  lastOutcome?: {
    outcome: string
    completedAt: number
  }
  nextScheduled?: number
}

export function MedicationCard({
  medication,
  onEdit,
  lastOutcome,
  nextScheduled,
}: MedicationCardProps) {
  const { t } = useTranslation()
  const { showToast } = useToast()
  const removeMedication = useMutation(api.medications.remove)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      const result = await removeMedication({ medicationId: medication._id })
      if (result.success) {
        showToast({
          message: t('medicationManagement.toast.medicationDeleted', { name: medication.name }),
          type: 'success',
        })
      } else {
        showToast({
          message: result.error || t('errors.generic'),
          type: 'error',
        })
      }
    } catch {
      showToast({ message: t('errors.generic'), type: 'error' })
    } finally {
      setIsDeleting(false)
      setShowDeleteConfirm(false)
    }
  }

  const getTimeSlotLabel = (slot: string): string => {
    return t(`medicationManagement.timeSlots.${slot}`, slot)
  }

  const getOutcomeLabel = (outcome: string): string => {
    const labels: Record<string, string> = {
      taken: t('medications.taken', 'Taken'),
      taking_now: t('medications.takingNow', 'Taken'),
      will_take_later: t('medications.willTakeLater', 'Will take later'),
      no_answer: t('medications.noAnswer', 'No answer'),
      missed: t('medications.missed', 'Missed'),
    }
    return labels[outcome] || outcome
  }

  const formatLastTaken = (timestamp: number, outcome: string): string => {
    const date = new Date(timestamp)
    const now = new Date()
    const isToday = date.toDateString() === now.toDateString()
    const isYesterday = date.toDateString() === new Date(now.getTime() - 86400000).toDateString()

    const timeStr = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    const outcomeLabel = getOutcomeLabel(outcome)

    if (outcome === 'taken' || outcome === 'taking_now') {
      if (isToday) return `${t('medications.lastTaken', 'Last taken')}: Today at ${timeStr}`
      if (isYesterday) return `${t('medications.lastTaken', 'Last taken')}: Yesterday at ${timeStr}`
      return `${t('medications.lastTaken', 'Last taken')}: ${date.toLocaleDateString()}`
    }

    if (isYesterday) return `${outcomeLabel} yesterday`
    return outcomeLabel
  }

  const formatNextScheduled = (timestamp: number): string => {
    const date = new Date(timestamp)
    const now = new Date()
    const isToday = date.toDateString() === now.toDateString()
    const isTomorrow = date.toDateString() === new Date(now.getTime() + 86400000).toDateString()

    const timeStr = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })

    if (isToday) return `${t('medications.next', 'Next')}: Today at ${timeStr}`
    if (isTomorrow) return `${t('medications.next', 'Next')}: Tomorrow at ${timeStr}`
    return `${t('medications.next', 'Next')}: ${date.toLocaleDateString()}`
  }

  const cardStyles: React.CSSProperties = {
    padding: '1rem',
    borderBottom: '1px solid #f3f4f6',
  }

  const topRowStyles: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  }

  const nameStyles: React.CSSProperties = {
    fontSize: '16px',
    fontWeight: 500,
    color: '#1a1a1a',
  }

  const timeStyles: React.CSSProperties = {
    fontSize: '14px',
    color: '#6b7280',
    marginTop: '0.25rem',
  }

  const scheduleStyles: React.CSSProperties = {
    fontSize: '13px',
    color: '#2563eb',
    marginTop: '0.25rem',
  }

  const notesStyles: React.CSSProperties = {
    fontSize: '13px',
    color: '#9ca3af',
    marginTop: '0.25rem',
    fontStyle: 'italic',
  }

  const actionsStyles: React.CSSProperties = {
    display: 'flex',
    gap: '0.5rem',
    flexShrink: 0,
  }

  const iconButtonStyles: React.CSSProperties = {
    minWidth: '44px',
    minHeight: '44px',
    width: '44px',
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    fontSize: '18px',
    padding: 0,
  }

  const confirmOverlayStyles: React.CSSProperties = {
    marginTop: '0.75rem',
    padding: '0.75rem',
    backgroundColor: '#fef2f2',
    borderRadius: '8px',
    border: '1px solid #fecaca',
  }

  const confirmTextStyles: React.CSSProperties = {
    fontSize: '14px',
    color: '#dc2626',
    marginBottom: '0.75rem',
  }

  const confirmButtonsStyles: React.CSSProperties = {
    display: 'flex',
    gap: '0.5rem',
  }

  const statusRowStyles: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '0.75rem',
    paddingTop: '0.75rem',
    borderTop: '1px solid #f3f4f6',
    fontSize: '13px',
  }

  const lastStatusStyles = (outcome?: string): React.CSSProperties => {
    let color = '#6b7280'
    if (outcome === 'taken' || outcome === 'taking_now') color = '#22c55e'
    else if (outcome === 'no_answer' || outcome === 'missed') color = '#ef4444'
    else if (outcome === 'will_take_later') color = '#eab308'

    return { color }
  }

  return (
    <div style={cardStyles}>
      <div style={topRowStyles}>
        <div>
          <div style={nameStyles}>{medication.name}</div>
          <div style={timeStyles}>
            {getTimeSlotLabel(medication.timeSlot)}
            {medication.specificTime && ` \u2022 ${medication.specificTime}`}
          </div>
          {medication.frequency && medication.frequency !== 'daily' && (
            <div style={scheduleStyles}>{formatScheduleText(medication)}</div>
          )}
          {medication.notes && <div style={notesStyles}>{medication.notes}</div>}
        </div>
        <div style={actionsStyles}>
          <button
            style={iconButtonStyles}
            onClick={() => onEdit(medication)}
            aria-label={t('medicationManagement.editMedication')}
            title={t('medicationManagement.editMedication')}
          >
            &#9998;
          </button>
          <button
            style={{
              ...iconButtonStyles,
              borderColor: showDeleteConfirm ? '#fecaca' : '#e5e7eb',
              backgroundColor: showDeleteConfirm ? '#fef2f2' : '#fff',
            }}
            onClick={() => setShowDeleteConfirm(!showDeleteConfirm)}
            aria-label={t('medicationManagement.deleteMedication')}
            title={t('medicationManagement.deleteMedication')}
          >
            &#128465;
          </button>
        </div>
      </div>

      {/* Status row from medication reminders - shows last outcome and next scheduled */}
      {(lastOutcome || nextScheduled) && (
        <div style={statusRowStyles}>
          <span style={lastStatusStyles(lastOutcome?.outcome)}>
            {lastOutcome
              ? formatLastTaken(lastOutcome.completedAt, lastOutcome.outcome)
              : t('medications.noHistory', 'No history yet')}
          </span>
          {nextScheduled && (
            <span style={{ color: '#6b7280' }}>{formatNextScheduled(nextScheduled)}</span>
          )}
        </div>
      )}

      {showDeleteConfirm && (
        <div style={confirmOverlayStyles}>
          <div style={confirmTextStyles}>
            {t('medicationManagement.deleteConfirm.message', { name: medication.name })}
          </div>
          <div style={confirmButtonsStyles}>
            <Button
              variant="outline"
              size="small"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isDeleting}
            >
              {t('medicationManagement.deleteConfirm.cancel')}
            </Button>
            <Button
              size="small"
              onClick={handleDelete}
              disabled={isDeleting}
              style={{ backgroundColor: '#dc2626' }}
            >
              {isDeleting ? t('common.saving') : t('medicationManagement.deleteConfirm.confirm')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
